/**
 * reviewerLogic.js
 * Prepares payload + strict instructions for Reviewer Generator API.
 * ✅ Updated: Math-content detection + "Solving MCQ" enforcement for math materials.
 * ✅ Added: Batch payload generation for very large documents.
 * ✅ FIXED: In batch mode, questionCount + flashcardCount are TOTAL for whole doc.
 * ✅ NEW: Hard Mode logic (Uniform choice lengths & strict distractors).
 * ✅ FIXED: Summary prompts now use flexible ranges to prevent "fluff" generation.
 */

const ALLOWED_QUIZ_TYPES = new Set([
  "true-false",
  "identification",
  "fill-blanks",
  "mcq",
  "mixed",
]);

const ALLOWED_DIFFICULTIES = new Set(["easy", "medium", "hard"]);

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const normalizeWhitespace = (s) =>
  String(s || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const sanitizeUserNotes = (s) => {
  return normalizeWhitespace(s)
    .replace(/```[\s\S]*?```/g, "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .slice(0, 2000);
};

const isLikelyAlreadyNotes = (content) => {
  const lines = normalizeWhitespace(content).split("\n").slice(0, 60);
  const bulletish = lines.filter((l) =>
    /^(\d+[\).\s-]|[-•*]\s)/.test(l.trim())
  ).length;
  return bulletish >= Math.max(10, Math.floor(lines.length * 0.35));
};

const normalizeDifficulty = (d) => {
  if (typeof d === "number" && Number.isFinite(d)) {
    if (d === 1) return "easy";
    if (d === 2) return "medium";
    if (d === 3) return "hard";
  }
  if (typeof d === "string") {
    const v = d.trim().toLowerCase();
    if (v === "1") return "easy";
    if (v === "2") return "medium";
    if (v === "3") return "hard";
    if (ALLOWED_DIFFICULTIES.has(v)) return v;
  }
  return "medium";
};

const estimateFlashcardCount = (content, questionCount, summaryEnabled, quizEnabled) => {
  const base = Math.round((content.length || 0) / 380);
  let derived = clamp(base, 10, 22);
  const qc = Number.isFinite(questionCount) ? questionCount : 10;

  if (summaryEnabled && quizEnabled) {
    derived = clamp(Math.round((derived + qc) / 3), 8, 14);
  } else {
    derived = clamp(Math.round((derived + qc) / 2), 10, 22);
  }
  return derived;
};

const estimateSummaryMainPoints = (content) => {
  const base = Math.round((content.length || 0) / 320);
  return clamp(base, 12, 26);
};

// ======================================================================
// ✅ MATH/CALCULATION HEURISTIC
// ======================================================================
const detectMathyContent = (text) => {
  const s = String(text || "");
  if (!s.trim()) return false;

  let score = 0;

  // Strong signals
  if (/[\\$][\s\S]*?(frac|sqrt|cdot|times|left|right|sum|int|lim)/i.test(s)) score += 3;
  if (/<sup|<sub/i.test(s)) score += 2;

  // Equations/operators
  if (/[=]/.test(s)) score += 1;
  if (/[+\-*/^]/.test(s)) score += 1;

  // Math/physics keywords
  if (/\b(equation|solve|simplify|derive|derivative|integral|limit|logarithm|probability|statistics|matrix|vector|scalar)\b/i.test(s)) score += 2;
  if (/\b(velocity|acceleration|force|energy|power|work|momentum|mass|density|pressure|voltage|current|resistance|ohm|newton|joule|watt)\b/i.test(s)) score += 2;

  // Numbers/units
  if (/\b\d+(\.\d+)?\b/.test(s)) score += 1;
  if (/\b(cm|mm|m|km|g|kg|s|ms|h|hr|N|J|W|Pa|V|A|Ω|ohm)\b/i.test(s)) score += 1;

  return score >= 4;
};

// ======================================================================
// ✅ STRICT JSON OUTPUT RULES
// ======================================================================
const SYSTEM_PROMPTS = {
  BASE: `STRICT SYSTEM INSTRUCTION: You are a Reviewer Generator API. You must return valid JSON only. Do not add markdown formatting (like \`\`\`json\`). Do not add any text before or after the JSON.

Your output MUST be a single JSON object with EXACTLY this structure (include all keys always):
{
  "summary": "A string containing valid HTML only. Use <h3> for Module titles, <b> for bolding, and <ul>/<li> for lists. Do NOT use markdown in this string.",
  "flashcards": [
    { "id": 1, "front": "Concept/Question", "back": "Definition/Answer" }
  ],
  "quiz": [
    { 
      "question": "Question text", 
      "options": ["Option A", "Option B"], 
      "answer": "Correct Answer",
      "explanation": "Step-by-step solution or context. If not needed, use empty string."
    }
  ]
}

HARD RULES (JSON):
1) Output MUST be parseable JSON (double quotes, no trailing commas).
2) Never include code fences, markdown, or commentary.

HARD RULES (HTML in summary):
1) Allowed tags: <h3>, <div>, <b>, <ul>, <li>, <br>. Use only these.
2) Do NOT output <script>, <style>, <img>, <a>, or any event handlers.
3) Use <div style="margin-bottom: 20px;"> wrappers for sections.
4) Use <b>1. [Concept Name]</b> for main points and <ul><li> for details.

CONTENT RULES:
1) Generate content STRICTLY from the provided text.
2) Do not quote long sentences verbatim; REPHRASE for clarity.
3) If summaryEnabled is false: set "summary" to "<div style='margin-bottom: 20px;'><b>Summary disabled.</b></div>".
   If flashcardsEnabled is false: set "flashcards" to [].
   If quizEnabled is false: set "quiz" to [].
`,
};

const QUIZ_DEFINITIONS = {
  "true-false": `TYPE: True/False
- "options" MUST always be ["True", "False"].
- "answer" MUST be either "True" or "False".
- "explanation" must be a brief justification or "".`,

  "identification": `TYPE: Identification
- STRUCTURE: The 'question' MUST be the DEFINITION/DESCRIPTION.
- The 'answer' MUST be the specific TERM being defined.
- The "options" array MUST be [].
- STRICT CONSTRAINT: The answer MUST be 1 or 2 words ONLY. NOUNS only.
- "explanation" must be "" or a very short context.`,

  "fill-blanks": `TYPE: Fill-in-the-Blank
- The question must contain ONE blank represented by "_______".
- The "options" array MUST be [].
- The "answer" is the word/phrase that fits the blank.
- "explanation" must be "" or a short hint.`,

  "mcq": `TYPE: Multiple Choice (MCQ)
- "options" MUST contain 4 distinct choices.
- "answer" MUST be the full text of the correct option.
- STRICT OPTION LENGTH SYMMETRY: All 4 choices MUST have approximately the same character count and word count (target +/-10%).
- The correct option MUST NOT be the uniquely longest or most detailed choice.
- If the correct option is detailed/longer, strengthen and expand all 3 distractors to match its length and specificity before final output.
- Distractors must be plausible based on the text.
- "explanation" must be "" for non-math, but REQUIRED with steps for solving/math questions.`,
};

// ======================================================================
// ✅ CHOICE BALANCING RULES
// ======================================================================
const getHardModeOptionRules = (difficulty) => {
  const baseRules = `
### MCQ CHOICE BALANCE RULES (MANDATORY) ###
1. **UNIFORM LENGTH:** Ensure all 4 MCQ options are roughly the same character length and word count to prevent guessing based on length.
2. **NO OBVIOUS ANSWERS:** Do NOT make the correct choice longer or more detailed than the distractors.
3. **SELF-CHECK BEFORE OUTPUT:** Inspect all 4 choices. If the correct choice is longer, expand the other 3 choices to balance them evenly.
`;

  if (difficulty !== "hard") return baseRules;

  return `
${baseRules}
### CRITICAL OVERRIDE: HARD MODE ACTIVE ###
1. **NO LAZY COPYING:** Do not just copy-paste sentences from the text. Rephrase concepts to test understanding.
2. **PLAUSIBLE DISTRACTORS:** Wrong answers must be technically plausible concepts from the same domain, not obvious errors.
3. **AVOID "ALL OF THE ABOVE":** Do not use "All of the above" or "None of the above" unless absolutely necessary.
4. **NEAR-MISS DISTRACTORS:** Make distractors close alternatives that fail on subtle constraints.
5. **ANSWER BALANCE:** Spread correct options across A/B/C/D without a detectable pattern.
6. **EXPLANATION RIGOR:** Explain why the correct option is best and why at least one strong distractor is wrong.
`;
};

// ======================================================================
// ✅ MATH "SOLVING MCQ" RULES (Updated for Hard Mode)
// ======================================================================
const getMathSolvingRules = (solvingCount, difficulty) => {
  const sc = Math.max(1, solvingCount || 1);
  const hardModeConstraint = difficulty === "hard" 
    ? "- Ensure all 4 calculated options are formatted with the SAME number of decimal places or sig-figs to prevent identifying the answer by precision." 
    : "";

  return `MATH SOLVING MCQ MODE (REQUIRED IF MATERIAL IS MATH/PHYSICS/CALCULATION):
- At least ${sc} of the quiz items MUST be SOLVING problems (not definitions).
- A SOLVING problem means:
  1) The question gives values or a clear equation/formula context from the text.
  2) The student must compute/simplify/solve to get the answer.
  3) Provide 4 MCQ options; exactly 1 is correct.
  4) "answer" MUST match the correct option text EXACTLY.
  5) "explanation" MUST show step-by-step solving and include the final result.
- Create problems ONLY from formulas/relationships/examples in the provided content.
- If the source has a formula: generate new numbers but same concept (do NOT invent new topics).
- Distractors must reflect common mistakes (sign error, wrong substitution, unit error, rounding).
- Use LaTeX formatting ($...$) for ALL math expressions in "question", "options", "answer", and "explanation".
- Keep the question solvable with the information given.
${hardModeConstraint}
`;
};

// ======================================================================
// ✅ QUIZ INSTRUCTION BUILDER
// ======================================================================
const getQuizInstructions = (quizTypesInput, totalCount, isMathContent, difficulty) => {
  const qc = Number.isFinite(totalCount) ? clamp(totalCount, 1, 60) : 10;

  let selectedTypes = [];
  if (Array.isArray(quizTypesInput)) {
    selectedTypes = quizTypesInput;
  } else if (typeof quizTypesInput === "string") {
    if (quizTypesInput === "mixed") {
      selectedTypes = ["mcq", "identification", "fill-blanks", "true-false"];
    } else {
      selectedTypes = [quizTypesInput];
    }
  }

  selectedTypes = selectedTypes.filter((t) => QUIZ_DEFINITIONS[t]);
  if (selectedTypes.length === 0) selectedTypes = ["mcq"];

  // Ensure MCQ exists in math mode
  if (isMathContent && !selectedTypes.includes("mcq")) {
    selectedTypes = ["mcq", ...selectedTypes];
  }

  const typeCount = selectedTypes.length;
  const baseCount = Math.floor(qc / typeCount);
  let remainder = qc % typeCount;

  let distributionString = "";
  let rulesString = "";

  selectedTypes.forEach((type) => {
    const countForThisType = baseCount + (remainder > 0 ? 1 : 0);
    remainder--;

    if (countForThisType > 0) {
      distributionString += `- ${countForThisType} ${type.toUpperCase()} questions\n`;
      rulesString += `\n[RULES FOR ${type.toUpperCase()}]:\n${QUIZ_DEFINITIONS[type]}\n`;
    }
  });

  const solvingCount = isMathContent ? clamp(Math.round(qc * 0.6), 1, qc) : 0;
  
  // ✅ Inject Hard Mode Rules
  const hardModeRules = getHardModeOptionRules(difficulty);
  const mathSolvingBlock = isMathContent ? `\n${getMathSolvingRules(solvingCount, difficulty)}\n` : "";

  return `QUIZ GENERATION RULES:
You MUST generate EXACTLY ${qc} quiz items total.
The quiz types must be distributed as follows:
${distributionString}

${difficulty === "hard" ? `HARD MODE QUESTION DESIGN:
- Use "most defensible", "best next action", and trade-off prompts.
- Avoid pure textbook-definition recall unless required by question type.
- Require interpretation of context constraints, not surface keyword matching.` : ""}

${hardModeRules}

SPECIFIC FORMATTING RULES PER TYPE:
${rulesString}

${mathSolvingBlock}

IMPORTANT:
- Keep questions strictly grounded in the provided text.
- Include the "explanation" key for EVERY quiz item (use "" if not needed).`;
};

// ======================================================================
// ✅ BATCHING HELPERS
// ======================================================================
const splitIntoParagraphs = (text) => {
  const t = normalizeWhitespace(text);
  return t.split(/\n{2,}/g).map((p) => p.trim()).filter(Boolean);
};

const splitHugeParagraph = (p, hardMaxChars) => {
  if (p.length <= hardMaxChars) return [p];

  const parts = p.split(/(?<=[.!?])\s+/g);
  const out = [];
  let buf = "";

  for (const s of parts) {
    if ((buf + " " + s).trim().length > hardMaxChars) {
      if (buf.trim()) out.push(buf.trim());
      buf = s;
    } else {
      buf = (buf + " " + s).trim();
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
};

const splitContentIntoBatches = (rawText, opts = {}) => {
  const {
    maxCharsPerBatch = 35000,
    hardMaxChars = 50000,
    overlapChars = 500,
  } = opts;

  const paragraphs = splitIntoParagraphs(rawText).flatMap((p) =>
    splitHugeParagraph(p, hardMaxChars)
  );

  const batches = [];
  let cur = "";

  for (const p of paragraphs) {
    const next = (cur ? cur + "\n\n" : "") + p;

    if (next.length <= maxCharsPerBatch) {
      cur = next;
      continue;
    }

    if (cur.trim()) batches.push(cur.trim());

    if (overlapChars > 0 && batches.length > 0) {
      const prev = batches[batches.length - 1];
      const overlap = prev.slice(Math.max(0, prev.length - overlapChars));
      cur = (overlap.trim() ? overlap.trim() + "\n\n" : "") + p;
    } else {
      cur = p;
    }
  }

  if (cur.trim()) batches.push(cur.trim());
  return batches;
};

const distributeTotalAcrossBatches = (total, weights) => {
  const T = Math.max(1, Math.floor(Number(total) || 1));
  const w = weights.map((x) => Math.max(1, Number(x) || 1));
  const sumW = w.reduce((a, b) => a + b, 0);

  const raw = w.map((x) => (x / sumW) * T);
  const base = raw.map((x) => Math.floor(x));
  let remainder = T - base.reduce((a, b) => a + b, 0);

  const frac = raw
    .map((x, i) => ({ i, f: x - base[i] }))
    .sort((a, b) => b.f - a.f);

  for (let k = 0; k < frac.length && remainder > 0; k++) {
    base[frac[k].i] += 1;
    remainder -= 1;
  }
  return base;
};

// ✅ allow zeros (important when total < batches)
const distributeTotalAllowZero = (total, weights) => {
  const T = Math.max(0, Math.floor(Number(total) || 0));
  const n = weights.length;
  if (n === 0) return [];
  if (T === 0) return Array(n).fill(0);

  const w = weights.map((x) => Math.max(1, Number(x) || 1));

  if (T < n) {
    const order = w.map((val, i) => ({ i, val })).sort((a, b) => b.val - a.val);
    const out = Array(n).fill(0);
    for (let k = 0; k < T; k++) out[order[k].i] = 1;
    return out;
  }

  // total >= batches -> give each batch 1, then distribute remainder
  const base = Array(n).fill(1);
  const remainder = T - n;
  const extras = distributeTotalAcrossBatches(remainder, w);
  return base.map((b, i) => b + extras[i]);
};

const capAndKeepTotal = (counts, maxPerBatch) => {
  let overflow = 0;

  const capped = counts.map((c) => {
    if (c > maxPerBatch) {
      overflow += (c - maxPerBatch);
      return maxPerBatch;
    }
    return c;
  });

  if (overflow === 0) return capped;

  let guard = 0;
  while (overflow > 0 && guard < 100000) {
    guard++;
    let moved = false;
    for (let i = 0; i < capped.length && overflow > 0; i++) {
      if (capped[i] < maxPerBatch) {
        capped[i] += 1;
        overflow -= 1;
        moved = true;
      }
    }
    if (!moved) break;
  }

  if (overflow > 0) {
    throw new Error(
      `Cannot distribute totals: requested too large for maxPerBatch=${maxPerBatch}.`
    );
  }
  return capped;
};

// ======================================================================
// ✅ MAIN: SINGLE PAYLOAD (supports batchMeta)
// ======================================================================
export const prepareReviewerPayload = (rawContent, config, batchMeta = null) => {
  const {
    summaryEnabled = true,
    flashcardsEnabled = true,
    quizEnabled = true,
    difficulty: rawDifficulty = "medium",
    quizType = ["mcq"],
    questionCount = 10,
    flashcardCount = null,
    timePerQuestion = 30,
    customInstructions = "",
    eli5 = false,
  } = config || {};

  if (typeof rawContent !== "string") {
    throw new Error("No text content found. Please check your file or input.");
  }

  const content = normalizeWhitespace(rawContent);

  if (content.length < 50) {
    throw new Error("The content is too short (under 50 characters).");
  }

  if (!summaryEnabled && !flashcardsEnabled && !quizEnabled) {
    throw new Error("Please select at least one feature (Summary, Flashcards, or Quiz).");
  }

  if (quizEnabled) {
    const typesToCheck = Array.isArray(quizType) ? quizType : [quizType];
    const valid = typesToCheck.every((t) => ALLOWED_QUIZ_TYPES.has(t) || t === "mixed");
    if (!valid) throw new Error("Invalid quiz configuration.");
  }

  const difficulty = normalizeDifficulty(rawDifficulty);
  if (!ALLOWED_DIFFICULTIES.has(difficulty)) {
    throw new Error(`Invalid difficulty: ${rawDifficulty}`);
  }

  const isBatchMode = !!batchMeta && Number(batchMeta?.batchCount || 0) > 1;

  // ✅ qc can be 0 if quiz disabled (important for batch distribution)
  const qc = quizEnabled ? clamp(Number(questionCount) || 10, 1, 60) : 0;

  // ✅ flashcards can be 0 per batch
  let targetFlashcardCount = 0;
  if (flashcardsEnabled) {
    const hasFlashcardCount =
      flashcardCount !== null && flashcardCount !== undefined && flashcardCount !== "";

    if (hasFlashcardCount) {
      const minFc = isBatchMode ? 0 : 1;
      targetFlashcardCount = clamp(Number(flashcardCount), minFc, 50);
    } else {
      targetFlashcardCount = estimateFlashcardCount(content, qc, summaryEnabled, quizEnabled);
    }
  }

  // In batch mode: reduce required main points range
  const summaryMainPoints = isBatchMode
    ? clamp(Math.round((content.length || 0) / 420), 8, 16)
    : estimateSummaryMainPoints(content);

  const notesStyleRule = isLikelyAlreadyNotes(content)
    ? `INPUT SHAPE NOTE: The provided text looks like summarized notes/bullets.
- Do NOT echo the same bullets.
- REPHRASE and reorganize into clean sections.
- Expand each bullet into clearer sub-bullets.`
    : `INPUT SHAPE NOTE: The provided text looks like a source passage.
- Extract key concepts and rewrite into a detailed reviewer format.`;

  const isMathContent = detectMathyContent(content);

  // ✅ Pass difficulty to the quiz builder
  const specificQuizRule =
    quizEnabled && qc > 0 
      ? getQuizInstructions(quizType, qc, isMathContent, difficulty) 
      : "No quiz required.";

  const userNotes = sanitizeUserNotes(customInstructions);

  const carryOverRule = isBatchMode
    ? `
BATCH MODE:
- You are processing Batch ${batchMeta.batchIndex + 1} of ${batchMeta.batchCount}.
- Focus ONLY on this batch content.
- IMPORTANT: The FINAL numbered main point (#${summaryMainPoints}) MUST be:
  <b>${summaryMainPoints}. Carry-over context for next batch</b>
  and its <ul> MUST contain EXACTLY 5 bullets (what the next batch must remember).
`
    : "";

  // ✅ UPDATED: Flexible Summary Rules to prevent "fluff"
  const featureRules = `
FEATURE RULES:
- summaryEnabled=${summaryEnabled} → If false, summary must be "<div style='margin-bottom: 20px;'><b>Summary disabled.</b></div>"
- flashcardsEnabled=${flashcardsEnabled} → If true, generate EXACTLY ${targetFlashcardCount} flashcards.
- quizEnabled=${quizEnabled} → If true, generate EXACTLY ${qc} quiz items.

SUMMARY LENGTH + DEPTH RULES:
- Create a DETAILED summary.
- Target APPROXIMATELY ${summaryMainPoints} main sections (flexible range: ${Math.max(3, summaryMainPoints - 3)} to ${summaryMainPoints + 3}).
- Adapt the number of bullets per section (3-8) based on the actual density of information.
- Do NOT force 8 bullets if the concept is simple; avoid fluff.
- Group related concepts logically rather than fragmenting them just to hit a number count.

${carryOverRule}

HTML STRUCTURE:
- Use <h3>Module [Number]: [Title]</h3>.
- Only use: <h3>, <div>, <b>, <ul>, <li>, <br>.
`.trim();

  const readabilityRule = eli5
    ? `READABILITY: Use very simple words (ELI5), BUT remain strictly faithful to the text.`
    : `READABILITY: Use student-friendly wording.`;

  const mathModeBanner = isMathContent
    ? `MATH CONTENT DETECTED: Treat the topic as calculation/solving-oriented.
- Ensure many quiz questions require actual solving.
- "explanation" must show the steps for solving questions.`
    : `NON-MATH CONTENT: Solving steps are optional; explanation can be brief or "".`;

  const finalInstructions = `
${SYSTEM_PROMPTS.BASE}

${featureRules}

${specificQuizRule}

${mathModeBanner}

${notesStyleRule}

${readabilityRule}

USER PREFERENCES:
${userNotes || "None"}
`.trim();

  return {
    content,
    difficulty,
    eli5,
    summaryEnabled,
    flashcardsEnabled,
    quizEnabled,
    questionCount: qc,
    flashcardCount: targetFlashcardCount,
    timePerQuestion,
    quizType,
    customInstructions: finalInstructions,
    userInstruction: userNotes || "",
    isMathContent,
    batchMeta: batchMeta || null,
  };
};

// ======================================================================
// ✅ BATCH PAYLOADS (FIXED TOTAL COUNTS)
// ======================================================================
export const prepareReviewerBatchPayloads = (rawContent, config, batchOptions = {}) => {
  const {
    maxCharsPerBatch = 35000,
    hardMaxChars = 50000,
    overlapChars = 500,

    // ✅ Default: TOTAL across all batches
    totalQuestionsMode = batchOptions.totalQuestionsMode ?? config?.totalQuestionsMode ?? true,
    totalFlashcardsMode = batchOptions.totalFlashcardsMode ?? config?.totalFlashcardsMode ?? true,

    // caps (TOTAL mode uses these only as max per batch)
    maxQuestionsPerBatch = batchOptions.maxQuestionsPerBatch ?? config?.maxQuestionsPerBatch ?? 60,
    maxFlashcardsPerBatch = batchOptions.maxFlashcardsPerBatch ?? config?.maxFlashcardsPerBatch ?? 50,
  } = batchOptions;

  if (typeof rawContent !== "string") {
    throw new Error("No text content found. Please check your file or input.");
  }

  const normalized = normalizeWhitespace(rawContent);

  const batches = splitContentIntoBatches(normalized, {
    maxCharsPerBatch,
    hardMaxChars,
    overlapChars,
  });

  const batchCount = batches.length;
  if (batchCount === 0) throw new Error("No batches produced.");

  const weights = batches.map((b) => b.length);

  // -------------------------
  // QUIZ distribution
  // -------------------------
  let perBatchQuiz = Array(batchCount).fill(0);

  if (config?.quizEnabled) {
    const requestedTotalQuiz = Math.max(0, Math.floor(Number(config.questionCount ?? 10)));

    if (totalQuestionsMode) {
      perBatchQuiz = distributeTotalAllowZero(requestedTotalQuiz, weights);
      perBatchQuiz = capAndKeepTotal(perBatchQuiz, Number(maxQuestionsPerBatch || 60));
    } else {
      // old behavior: PER BATCH
      const per = clamp(Number(config.questionCount) || 10, 1, 60);
      perBatchQuiz = Array(batchCount).fill(per);
    }
  }

  // -------------------------
  // FLASHCARD distribution
  // -------------------------
  let perBatchFlash = Array(batchCount).fill(0);

  if (config?.flashcardsEnabled) {
    const hasFc =
      config.flashcardCount !== null &&
      config.flashcardCount !== undefined &&
      config.flashcardCount !== "";

    const requestedTotalFlash = hasFc
      ? Math.max(0, Math.floor(Number(config.flashcardCount)))
      : Math.max(
          0,
          Math.floor(
            estimateFlashcardCount(
              normalized,
              Number(config.questionCount || 10),
              !!config.summaryEnabled,
              !!config.quizEnabled
            )
          )
        );

    if (totalFlashcardsMode) {
      perBatchFlash = distributeTotalAllowZero(requestedTotalFlash, weights);
      perBatchFlash = capAndKeepTotal(perBatchFlash, Number(maxFlashcardsPerBatch || 50));
    } else {
      // old behavior: PER BATCH
      const per = clamp(Number(config.flashcardCount) || 10, 1, 50);
      perBatchFlash = Array(batchCount).fill(per);
    }
  }

  // -------------------------
  // Build payloads (disable quiz/flashcards if assigned 0)
  // -------------------------
  const payloads = batches.map((batchText, i) => {
    const qCount = perBatchQuiz[i] || 0;
    const fCount = perBatchFlash[i] || 0;

    const cfg = {
      ...(config || {}),

      quizEnabled: !!config?.quizEnabled && qCount > 0,
      questionCount: qCount,

      flashcardsEnabled: !!config?.flashcardsEnabled && fCount > 0,
      flashcardCount: fCount,
    };

    return prepareReviewerPayload(batchText, cfg, {
      batchIndex: i,
      batchCount,
      approxChars: batchText.length,
    });
  });

  return { batchCount, payloads };
};
